
"use client";

import { createContext, useContext, useEffect, useState, type ReactNode, useCallback } from 'react';
import { type OfferPartner, gamePartners as fallbackGamePartners } from '@/lib/data';
import { db } from '@/lib/firebase';
import { collection, getDocs, addDoc, doc, updateDoc, deleteDoc, query, orderBy, writeBatch } from 'firebase/firestore';
import { useToast } from '@/hooks/use-toast';

interface GamePartnerContextType {
  gamePartners: OfferPartner[];
  addGamePartner: (partner: Omit<OfferPartner, 'id'>) => Promise<void>;
  updateGamePartner: (id: string, updatedPartner: Partial<Omit<OfferPartner, 'id'>>) => Promise<void>;
  deleteGamePartner: (id: string) => Promise<void>;
  loading: boolean;
  refreshGamePartners: () => void;
}

const GamePartnerContext = createContext<GamePartnerContextType | undefined>(undefined);

export const GamePartnerProvider = ({ children }: { children: ReactNode }) => {
  const [gamePartners, setGamePartners] = useState<OfferPartner[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchGamePartners = useCallback(async () => {
    setLoading(true);
    try {
      const partnersCollection = collection(db, 'gamePartners');
      const q = query(partnersCollection, orderBy('name', 'asc'));
      const snapshot = await getDocs(q);

      const partnerList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
      } as OfferPartner));
      setGamePartners(partnerList);

    } catch (error) {
      console.error("Failed to fetch game partners from Firestore", error);
      toast({ variant: 'destructive', title: "Error", description: "Could not load game partners from the database." });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchGamePartners();
  }, [fetchGamePartners]);

  const addGamePartner = async (partnerData: Omit<OfferPartner, 'id'>) => {
    try {
      const partnersCollection = collection(db, 'gamePartners');
      const docRef = await addDoc(partnersCollection, partnerData);
      setGamePartners(prev => [...prev, {...partnerData, id: docRef.id}].sort((a,b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error("Error adding game partner:", error);
      toast({ variant: 'destructive', title: "Error", description: "Could not add the game partner." });
      throw error;
    }
  };

  const updateGamePartner = async (id: string, updatedPartnerData: Partial<Omit<OfferPartner, 'id'>>) => {
     try {
       if (Object.keys(updatedPartnerData).length === 0) {
           toast({ title: "No changes to save." });
           return;
       }

       const partnerDocRef = doc(db, 'gamePartners', id);
       await updateDoc(partnerDocRef, updatedPartnerData);
       
       setGamePartners(prev =>
         prev.map(p => (p.id === id ? { ...p, ...updatedPartnerData } : p)).sort((a,b) => a.name.localeCompare(b.name))
       );
     } catch (error) {
       console.error("Error updating game partner:", error);
       toast({ variant: 'destructive', title: "Error", description: "Could not update the game partner." });
       throw error;
     }
  };

  const deleteGamePartner = async (id: string) => {
    try {
      const partnerDocRef = doc(db, 'gamePartners', id);
      await deleteDoc(partnerDocRef);
      setGamePartners(prev => prev.filter(p => p.id !== id));
    } catch (error) {
       console.error("Error deleting game partner:", error);
       toast({ variant: 'destructive', title: "Error", description: "Could not delete the game partner." });
       throw error;
    }
  };

  return (
    <GamePartnerContext.Provider value={{ gamePartners, addGamePartner, updateGamePartner, deleteGamePartner, loading, refreshGamePartners: fetchGamePartners }}>
      {children}
    </GamePartnerContext.Provider>
  );
};

export const useGamePartners = () => {
  const context = useContext(GamePartnerContext);
  if (context === undefined) {
    throw new Error('useGamePartners must be used within a GamePartnerProvider');
  }
  return context;
};
